@if ((isLoading$ | async) || (isTableLoading$ | async)) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

<div class="flex flex-col lg:flex-row space-x-4 w-full">
  <div class="w-full lg:w-[70%] min-h-[24rem]">
    @if (chartView$ | async; as view) {
      @switch (view.type) {
        @case ('error') {
          <div class="p-4 bg-yellow-50 border-l-4 border-yellow-400 dark:bg-yellow-950/40 dark:border-yellow-800">
            <div class="flex">
              <div class="shrink-0">
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  data-slot="icon"
                  aria-hidden="true"
                  class="text-yellow-400 dark:text-yellow-300 size-5"
                >
                  <path
                    d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495ZM10 5a.75.75 0 0 1 .75.75v3.5a.75.75 0 0 1-1.5 0v-3.5A.75.75 0 0 1 10 5Zm0 9a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                    clip-rule="evenodd"
                    fill-rule="evenodd"
                  />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-yellow-700 dark:text-yellow-200">
                  {{ view.error.key }}: {{ view.error.value | transloco }}
                </p>
              </div>
            </div>
          </div>
        }
        @case ('chart') {
          <cc-line-chart
            [data]="view.chartData"
            class="w-full"
            [showLegend]="legend"
            (pointClickEvent)="pointClickEvent($event)"
          ></cc-line-chart>
        }
      }
    }
  </div>
  <div
    class="flex flex-col space-y-4 w-full lg:w-[30%]"
    *ngVar="identifier$ | async; let identifier"
  >
    @if (
      identifier?.summaryType !== 'COMPACT' && details$ | async;
      as details
    ) {
      @if (details.deliveryDay) {
        <h4 class="text-center text-gray-500 text-xl">
          {{ 'market-dashboard-details.DELIVERY_DAY' | transloco }}
        </h4>
      }
      <table class="table-fixed">
        <tbody>
          @if (details.priceDetails.intradayPrice) {
            <tr>
              <td class="w-56">
                {{ 'market-dashboard-details.INTRA_DAY_PRICE' | transloco }}
                <div class="text-xs font-bold">
                  ({{ details.priceDetails.intradayDateTime | date: 'short' }})
                </div>
              </td>
              <td class="w-28">
                {{ details.priceDetails.intradayPrice | number: '.2-2' }}
              </td>
              <td>{{ details.priceDetails.unit }}</td>
            </tr>
          }
          @if (details.priceDetails.lastClosingPrice) {
            <tr>
              <td>
                {{
                  (!hideClosingText
                    ? 'market-dashboard-details.CLOSING_PRICE'
                    : 'market-dashboard-details.CLOSING_VALUE'
                  ) | transloco
                }}
                <div class="text-xs font-bold">
                  ({{ details.priceDetails.lastClosingDate | date }})
                </div>
              </td>
              <td>
                {{ details.priceDetails.lastClosingPrice | number: '.2-2' }}
              </td>
              <td>{{ details.priceDetails.unit }}</td>
            </tr>
          }
          @if (details.priceDetails.intradayChange) {
            <tr>
              <td>
                {{ 'market-dashboard-details.INTRA_DAY_CHANGES' | transloco }}
              </td>
              <td>
                {{ details.priceDetails.intradayChange * 100 | number: '.2-2' }}
              </td>
              <td>%</td>
            </tr>
          }
          <tr>
            <td>
              {{ 'market-dashboard-details.YEAR_HIGH' | transloco }}
            </td>
            <td>{{ details.priceDetails.yearHigh | number: '.2-2' }}</td>
            <td>{{ details.priceDetails.unit }}</td>
          </tr>
          <tr>
            <td>
              {{ 'market-dashboard-details.YEAR_LOW' | transloco }}
            </td>
            <td>{{ details.priceDetails.yearLow | number: '.2-2' }}</td>
            <td>{{ details.priceDetails.unit }}</td>
          </tr>
          <tr>
            <td>
              {{
                'market-dashboard-details.CHANGE_DATE'
                  | transloco
                    : {
                        value:
                          (details.priceDetails.firstDayOfYear | date: 'short'),
                      }
              }}
            </td>
            <td>
              {{
                details.priceDetails.firstDayOfYearDiff * 100 | number: '.2-2'
              }}
            </td>
            <td>%</td>
          </tr>
          <tr>
            <td>
              {{ 'market-dashboard-details.ONE_YEAR_HIGH' | transloco }}
            </td>
            <td>{{ details.priceDetails.oneYearHigh | number: '.2-2' }}</td>
            <td>{{ details.priceDetails.unit }}</td>
          </tr>
          <tr>
            <td>
              {{ 'market-dashboard-details.ONE_YEAR_LOW' | transloco }}
            </td>
            <td>{{ details.priceDetails.oneYearLow | number: '.2-2' }}</td>
            <td>{{ details.priceDetails.unit }}</td>
          </tr>
          <tr>
            <td>
              {{
                'market-dashboard-details.CHANGE_DATE'
                  | transloco
                    : {
                        value:
                          (details.priceDetails.oneYearChangeDate
                          | date: 'short'),
                      }
              }}
            </td>
            <td>
              {{ details.priceDetails.oneYearChange * 100 | number: '.2-2' }}
            </td>
            <td>%</td>
          </tr>
        </tbody>
      </table>
      @if (details.volumeDetails && details.volumeDetails.previewDayVolume) {
        <div>
          <h4 class="text-center">
            {{ 'market-dashboard-details.VOLUME_TITLE' | transloco }}
          </h4>
          <table class="table-fixed">
            <tbody>
              <tr>
                <td class="w-56">
                  {{
                    'market-dashboard-details.PREVIEW_DAY_VOLUME' | transloco
                  }}
                </td>
                <td class="w-28">
                  {{ details.volumeDetails.previewDayVolume | number: '.2-2' }}
                </td>
                <td>GWh</td>
              </tr>
              <tr>
                <td>
                  {{ 'market-dashboard-details.DIFF' | transloco }}
                </td>
                <td>{{ details.volumeDetails.diff * 100 | number: '.2-2' }}</td>
                <td>%</td>
              </tr>
            </tbody>
          </table>
        </div>
      }
    }

    <!-- compact summary -->
    @if (
      identifier?.summaryType === 'COMPACT' && detailsCompact$ | async;
      as detail
    ) {
      @if (detail.deliveryDay) {
        <h4 class="text-center text-gray-500 text-xl">
          {{ 'market-dashboard-details.DELIVERY_DAY' | transloco }}
        </h4>
      }
      <table>
        <tbody>
          <tr>
            <th colspan="3"></th>
          </tr>
          <tr>
            <td class="w-56">
              {{ 'market-dashboard-details.ACTUAL' | transloco }}
              <div class="text-xs font-bold">
                ({{ detail.currentValueDate | date: 'shortDate' }})
              </div>
            </td>
            <td>
              {{ detail.currentValue | number: '.2-3' }}
            </td>
            <td>
              {{ detail.unit }}
            </td>
          </tr>
          <tr>
            <td>
              {{ 'market-dashboard-details.PREVIOUS' | transloco }}
              <div class="text-xs">
                ({{ detail.previousValueDate | date: 'shortDate' }})
              </div>
            </td>
            <td>
              {{ detail.previousValue | number: '.2-3' }}
            </td>
            <td>
              {{ detail.unit }}
            </td>
          </tr>
          <tr>
            <td>
              {{ 'market-dashboard-details.DIFF' | transloco }}
            </td>
            <td>
              {{
                (calculationType$ | async) === 'RELATIVE'
                  ? (detail.differenceRelative * 100 | number: '.2-2')
                  : (detail.differenceAbsolute | number: '.2-3')
              }}
            </td>
            <td>
              {{
                (calculationType$ | async) === 'RELATIVE' ? '%' : detail.unit
              }}
            </td>
          </tr>
        </tbody>
      </table>
    }
  </div>
</div>
